import { AlertTriangle, BarChart3, Boxes, Lock, Network, ScanLine, Smartphone, Sparkles, Upload, Wallet } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { useScrollReveal } from './useScrollReveal';
import { useCountUp } from './useCountUp';
import { AiWalkthrough, PosWalkthrough } from './MiniWalkthrough';

const DEMOS = { pos: PosWalkthrough, ai: AiWalkthrough };

const FEATURES = [
  {
    id: 'feat-pos', short: 'Point of sale', icon: ScanLine, eyebrow: '01 / AT THE COUNTER',
    title: 'Every sale, settled in seconds.',
    body: 'Scan, discount and checkout without friction. Barcode-driven carts, held sales and clear receipts keep the queue moving on your busiest days, with every transaction reconciling against stock and patient history automatically.',
    statValue: 80, statSuffix: '%', statLabel: 'Fewer pricing errors',
    statBody: 'Barcode scanning catches mismatched prices and stock errors before checkout completes.',
    image: 'feature-pos', alt: 'Illustrative Ghanaian pharmacy cashier scanning a medicine box at a point-of-sale counter',
    demo: 'pos',
  },
  {
    id: 'feat-inventory', short: 'Inventory', icon: Boxes, eyebrow: '02 / ON THE SHELF',
    title: 'Stock you can actually trust.',
    body: 'Track batches, expiry dates and reorder points across every branch from one place. Low-stock and near-expiry alerts reach the right person before a shelf runs empty or a batch goes to waste.',
    statValue: 60, statSuffix: '%', statLabel: 'Fewer stockouts',
    statBody: 'Reorder alerts surface before popular lines run out at the counter.',
    image: 'feature-inventory', alt: 'Illustrative Ghanaian pharmacy stock clerk checking medicine shelves with a tablet',
  },
  {
    id: 'feat-portal', short: 'Patient portal', icon: Smartphone, eyebrow: '03 / BEYOND THE COUNTER',
    title: 'Care that follows the patient home.',
    body: 'Patients upload prescriptions, request refills and message your team from their phone, shared only with their permission. Staff see a complete, permissioned care history the moment a regular walks back in.',
    statValue: 70, statSuffix: '%', statLabel: 'Faster refill approvals',
    statBody: 'Patients request and approve refills from their phone instead of calling in.',
    image: 'feature-portal', alt: 'Illustrative Ghanaian patient checking a simple messaging app on her phone at home',
  },
  {
    id: 'feat-adr', short: 'Adverse reactions', icon: AlertTriangle, eyebrow: '04 / PATIENT SAFETY',
    title: "When something goes wrong, it gets recorded.",
    body: "Log suspected adverse drug reactions directly against a patient's record and medication history, with severity, outcome and follow-up captured in one structured form, not a sticky note or a staff member's memory.",
    statValue: 5, statSuffix: ' min', statLabel: 'To log a full reaction report',
    statBody: 'Severity, outcome and follow-up captured in one structured form.',
    image: 'feature-adr', alt: 'Illustrative Ghanaian pharmacist noting a patient concern on a clipboard during a consultation',
  },
  {
    id: 'feat-controlled', short: 'Controlled substances', icon: Lock, eyebrow: '05 / COMPLIANCE',
    title: 'Controlled drugs, under real control.',
    body: "Sales of controlled substances are gated behind a compliance form and a pharmacist's explicit sign-off, no quiet exceptions at the till. A searchable registry keeps a full, audit-ready trail of what left the shelf, to whom and when.",
    statValue: 100, statSuffix: '%', statLabel: 'Pharmacist-approved sales',
    statBody: "No controlled sale completes without a licensed pharmacist's review.",
    image: 'feature-controlled', alt: 'Illustrative Ghanaian pharmacist checking a controlled substances logbook beside a secure cabinet',
  },
  {
    id: 'feat-payroll', short: 'Payroll', icon: Wallet, eyebrow: '06 / YOUR TEAM',
    title: 'Payroll, without the spreadsheet scramble.',
    body: 'Run payroll for every branch with Ghana-aware deductions, allowances and payslips generated automatically. What used to take an afternoon of reconciling numbers now takes minutes, with a clean record for every pay cycle.',
    statValue: 90, statSuffix: '%', statLabel: 'Faster payroll runs',
    statBody: 'What took an afternoon now takes minutes, every pay cycle.',
    image: 'feature-payroll', alt: 'Illustrative Ghanaian pharmacy manager reviewing payroll on a laptop in a back office',
  },
  {
    id: 'feat-finance', short: 'Accounting & finance', icon: BarChart3, eyebrow: '07 / THE NUMBERS',
    title: 'Expenses and assets, finally in one ledger.',
    body: 'Capture day-to-day expenses with receipts attached, and keep a fixed asset register for equipment, fixtures and fittings across every branch. Finance gets a clear, exportable picture without chasing paper trails.',
    statValue: 100, statSuffix: '%', statLabel: 'Expenses matched to receipts',
    statBody: 'Expenses, receipts and assets recorded against the same ledger.',
    image: 'feature-finance', alt: 'Illustrative Ghanaian accountant reviewing receipts and a calculator at a desk',
  },
  {
    id: 'feat-branches', short: 'Multi-branch', icon: Network, eyebrow: '08 / EVERY LOCATION',
    title: 'One pharmacy group. One view.',
    body: 'Switch between branches without losing your place, and see stock, sales and staff performance side by side. Permissions shape what each role can see, so head-office oversight never means losing local control.',
    statValue: 12, statSuffix: '+', statLabel: 'Branches on one login',
    statBody: 'Switch locations without signing in again or losing context.',
    image: 'feature-branches', alt: 'Illustrative Ghanaian pharmacy owner viewing a multi-branch map on a tablet',
  },
  {
    id: 'feat-ai', short: 'AI assistant', icon: Sparkles, eyebrow: '09 / INTELLIGENT SUPPORT',
    title: 'An assistant that knows your pharmacy.',
    body: 'Staff can ask the Claude AI assistant about drug interactions, stock levels or store policy and get grounded answers in seconds, right inside the console. It supports the pharmacist\u2019s judgement; it never diagnoses, prescribes or replaces professional oversight.',
    statValue: 10, statSuffix: ' sec', statLabel: 'Average response time',
    statBody: 'Context-aware support available to staff whenever they need it.',
    image: 'feature-ai', alt: 'Illustrative Ghanaian pharmacist using a tablet with an AI chat assistant interface',
    demo: 'ai',
  },
  {
    id: 'feat-storage', short: 'Document storage', icon: Upload, eyebrow: '10 / EVERY RECORD, FILED',
    title: 'Every document finds its place.',
    body: "Capture a prescription with a phone camera and it's automatically resized, labelled and attached to the right patient, from the portal, the till or the client record. Branch staff are notified the moment a new document needs attention.",
    statValue: 2, statSuffix: 'MB', statLabel: 'Auto-shrink limit, every upload',
    statBody: 'Labelled documents stay attached to the patient they belong to.',
    image: 'feature-storage', alt: 'Illustrative Ghanaian pharmacy staff member photographing a prescription with a phone camera',
  },
];

const COMPARISON = [
  ['Point of sale', 'Handwritten receipts and manual price lookups', 'Barcode scanning, instant pricing and digital receipts'],
  ['Inventory', 'Physical stock counts and guesswork reorder timing', 'Live batch and expiry tracking with reorder alerts'],
  ['Patient portal', 'Patients call in and wait for a callback', 'Patients upload, request refills and message from their phone'],
  ['Adverse reactions', 'Noted on paper, if at all', "Logged against the patient's record with severity and outcome"],
  ['Controlled substances', 'Logbook entries trusted to memory', 'A compliance form and pharmacist sign-off on every sale'],
  ['Payroll', 'A spreadsheet rebuilt every month', 'Automatic payslips with Ghana-aware deductions'],
  ['Accounting & finance', 'Receipts in a drawer, reconciled by hand', 'Expenses and assets tracked in one ledger'],
  ['Multi-branch', 'Separate spreadsheets per branch', 'One login, every branch side by side'],
  ['AI assistant', 'Flipping through reference books mid-queue', 'Instant, grounded answers inside the console'],
  ['Document storage', 'Paper files that can go missing', 'Labelled, searchable documents attached to each patient'],
];

const FeatureImage = ({ name, alt, demo }) => {
  const Demo = demo && DEMOS[demo];
  return (
    <figure className="wl-feature-image">
      <img src={`/images/welcome/${name}.webp`} alt={alt} width="1000" height="671" loading="lazy" decoding="async" data-testid={`welcome-${name}-image`} />
      <span>AI-generated scene</span>
      {Demo && <Demo />}
    </figure>
  );
};

const FeatureRow = ({ feature, index }) => {
  const [ref, visible] = useScrollReveal();
  const count = useCountUp(feature.statValue, visible);
  const Icon = feature.icon;
  return (
    <article ref={ref} id={feature.id} className={`wl-feature${index % 2 === 1 ? ' is-reverse' : ''}${visible ? ' is-visible' : ''}`} data-testid={`welcome-feature-${feature.id}`}>
      <FeatureImage name={feature.image} alt={feature.alt} demo={feature.demo} />
      <div className="wl-feature-copy">
        <span className="wl-feature-eyebrow"><Icon size={17} /> {feature.eyebrow}</span>
        <h3>{feature.title}</h3>
        <p>{feature.body}</p>
        <div className="wl-feature-stat" data-testid={`welcome-feature-stat-${feature.id}`}>
          <div className="wl-feature-stat-number" aria-label={`${feature.statValue}${feature.statSuffix}`} data-testid={`welcome-feature-stat-number-${feature.id}`}>{count}{feature.statSuffix}</div>
          <div className="wl-feature-stat-copy">
            <strong>{feature.statLabel}</strong>
            <span>{feature.statBody}</span>
          </div>
          <em>Illustrative</em>
        </div>
      </div>
    </article>
  );
};

const ComparisonTable = () => (
  <div className="wl-compare" data-testid="welcome-feature-comparison">
    <h3 className="wl-compare-heading">The old way, compared.</h3>
    <table className="wl-compare-table" data-testid="welcome-comparison-table">
      <caption>Typical manual pharmacy processes vs. DutiLyne, feature by feature</caption>
      <thead>
        <tr><th scope="col">Pharmacy task</th><th scope="col">The manual way</th><th scope="col">With DutiLyne</th></tr>
      </thead>
      <tbody>
        {COMPARISON.map(([label, manual, withDutilyne], i) => (
          <tr key={label} data-testid={`welcome-comparison-row-${i}`}>
            <th scope="row">{label}</th>
            <td data-label="The manual way">{manual}</td>
            <td data-label="With DutiLyne">{withDutilyne}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="wl-small wl-compare-note" data-testid="welcome-comparison-disclosure">Comparisons describe typical manual processes observed across community pharmacies. They are illustrative, not measured benchmarks.</p>
  </div>
);

export const FeatureShowcase = () => (
  <section className="wl-section wl-showcase" id="showcase" aria-labelledby="showcase-heading" data-testid="welcome-feature-showcase">
    <div className="wl-wrap">
      <SectionHeading id="showcase-heading" eyebrow="EVERYTHING YOUR PHARMACY NEEDS" title={<>One platform.<br />Every part of the pharmacy.</>}>
        From the first scan at the till to the last entry in the controlled-substances register, DutiLyne covers the everyday and the regulated, in one connected system.
      </SectionHeading>
      <nav className="wl-showcase-nav" aria-label="Jump to a feature" data-testid="welcome-showcase-jumpnav">
        {FEATURES.map((feature) => <a key={feature.id} href={`#${feature.id}`} data-testid={`welcome-jump-${feature.id}`}>{feature.short}</a>)}
      </nav>
      <div className="wl-showcase-list">
        {FEATURES.map((feature, i) => <FeatureRow key={feature.id} feature={feature} index={i} />)}
      </div>
      <ComparisonTable />
    </div>
  </section>
);
